package com.evoapps.evoshift.domain.comparison

import com.evoapps.evoshift.domain.model.DailyRecord
import com.evoapps.evoshift.domain.model.LabourShare
import com.evoapps.evoshift.domain.model.Spmh
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.DayOfWeek
import kotlinx.datetime.LocalDate
import kotlinx.datetime.daysUntil
import kotlinx.datetime.isoDayNumber
import kotlinx.datetime.minus
import kotlinx.datetime.number
import kotlinx.datetime.plus
import kotlin.math.abs

/*
 * SH-225: sales comparisons over the daily records. A day (or a run of days) is set against the
 * days it is compared with:
 *
 *   LAST_WEEK          the same weekday 7 days earlier
 *   LAST_YEAR_WEEKDAY  the same weekday of the same ISO week a year earlier (Monday vs Monday); a
 *                      week 53 with no week 53 the year before is set against that year's week 52
 *   LAST_YEAR_DATE     the same calendar date a year earlier (24.12 vs 24.12); 29 February against
 *                      28 February
 *
 * Missing data is "no data", never 0: a day without takings recorded (revenue.realNetto null) is
 * left out of both sides of an aggregate, so a week to date is set against the same days of the
 * week before and the screens say how many days were compared. Public holidays on either side are
 * flagged (Polish holidays move with Easter). A change in % is undefined (null) when the base is
 * 0 or missing; the metrics are the net sales first, then the labour %, SPMH and the daily result.
 */

/** What a period is compared with. */
enum class ComparisonMode {
    LAST_WEEK,
    LAST_YEAR_WEEKDAY,
    LAST_YEAR_DATE;

    val isLastYear: Boolean get() = this != LAST_WEEK
}

/** The figures a comparison sets side by side. */
enum class ComparisonMetric {
    /** The net takings (revenue.realNetto). */
    NET_SALES,
    /** SH-228: the actual employer labour cost of the days with takings. */
    LABOUR_COST,
    /** The actual labour cost ÷ the income it is measured against (DailyRecord.labourPercentReal), in percent. */
    LABOUR_PERCENT,
    /** Net takings ÷ hours worked (Spmh). */
    SPMH,
    /** The daily result (income − costs, DayResults). */
    RESULT
}

/** An inclusive run of days from [start] to [end]. */
data class DateRange(val start: LocalDate, val end: LocalDate) {
    init {
        require(start <= end) { "a range ends on or after its start: $start..$end" }
    }

    /** How many days it holds. */
    val length: Int get() = start.daysUntil(end) + 1

    operator fun contains(date: LocalDate): Boolean = date in start..end

    /** Its days in order. */
    val dates: List<LocalDate> get() = (0 until length).map { start.plus(DatePeriod(days = it)) }

    /** The smallest range holding this one and [other]. */
    fun union(other: DateRange): DateRange = DateRange(minOf(start, other.start), maxOf(end, other.end))

    companion object {
        /** The calendar month of [date]. */
        fun monthOf(date: LocalDate): DateRange {
            val first = LocalDate(date.year, date.month, 1)
            return DateRange(first, first.plus(DatePeriod(months = 1)).minus(DatePeriod(days = 1)))
        }

        /** The smallest range holding all of [dates]; null without any. */
        fun covering(dates: Collection<LocalDate>): DateRange? =
            if (dates.isEmpty()) null else DateRange(dates.min(), dates.max())
    }
}

/** An ISO-8601 week: weeks start on Monday and week 1 is the one with the year's first Thursday. */
data class IsoWeek(val year: Int, val week: Int)

/** Where a day is compared: [date], and whether it had to move ([shiftedWeek], [shiftedDay]). */
data class Counterpart(
    val date: LocalDate,
    /** LAST_YEAR_WEEKDAY: a week 53 set against week 52 (the year before had no week 53). */
    val shiftedWeek: Boolean = false,
    /** LAST_YEAR_DATE: 29 February set against 28 February. */
    val shiftedDay: Boolean = false
)

object ComparisonCalendar {
    /** The Monday of [date]'s ISO week. */
    fun isoWeekStart(date: LocalDate): LocalDate = date.minus(DatePeriod(days = date.dayOfWeek.isoDayNumber - 1))

    /** [date]'s ISO week: the year of its week's Thursday and that Thursday's week of the year. */
    fun isoWeek(date: LocalDate): IsoWeek {
        val thursday = isoWeekStart(date).plus(DatePeriod(days = 3))
        return IsoWeek(thursday.year, (thursday.dayOfYear - 1) / 7 + 1)
    }

    /** 52 or 53: the ISO week of 28 December is always the year's last. */
    fun weeksInIsoYear(year: Int): Int = isoWeek(LocalDate(year, 12, 28)).week

    /** The [dayOfWeek] of ISO week [week] of ISO year [year]. */
    fun ofIsoWeek(year: Int, week: Int, dayOfWeek: DayOfWeek): LocalDate {
        // 4 January is always in week 1.
        val week1 = isoWeekStart(LocalDate(year, 1, 4))
        return week1.plus(DatePeriod(days = (week - 1) * 7 + dayOfWeek.isoDayNumber - 1))
    }

    /** The day [date] is compared with in [mode]. */
    fun counterpart(date: LocalDate, mode: ComparisonMode): Counterpart = when (mode) {
        ComparisonMode.LAST_WEEK -> Counterpart(date.minus(DatePeriod(days = 7)))
        ComparisonMode.LAST_YEAR_WEEKDAY -> {
            val week = isoWeek(date)
            val previousYear = week.year - 1
            val weeks = weeksInIsoYear(previousYear)
            val shifted = week.week > weeks
            Counterpart(ofIsoWeek(previousYear, minOf(week.week, weeks), date.dayOfWeek), shiftedWeek = shifted)
        }
        ComparisonMode.LAST_YEAR_DATE ->
            if (date.month.number == 2 && date.day == 29) Counterpart(LocalDate(date.year - 1, 2, 28), shiftedDay = true)
            else Counterpart(LocalDate(date.year - 1, date.month, date.day))
    }

    /** The days [range]'s days are compared with in [mode]: one run from the earliest to the latest. */
    fun counterpartRange(range: DateRange, mode: ComparisonMode): DateRange =
        DateRange.covering(range.dates.map { counterpart(it, mode).date })!!

    /** The first day of [date]'s week when weeks start on [firstDayOfWeek] (ISO, 1 = Monday … 7 = Sunday). */
    fun weekStart(date: LocalDate, firstDayOfWeek: Int): LocalDate {
        val first = firstDayOfWeek.coerceIn(1, 7)
        val back = (date.dayOfWeek.isoDayNumber - first).mod(7)
        return date.minus(DatePeriod(days = back))
    }

    /** The whole weeks (starting on [firstDayOfWeek]) that hold any day of [range], in order. */
    fun weeksCovering(range: DateRange, firstDayOfWeek: Int): List<DateRange> {
        val weeks = mutableListOf<DateRange>()
        var start = weekStart(range.start, firstDayOfWeek)
        while (start <= range.end) {
            weeks += DateRange(start, start.plus(DatePeriod(days = 6)))
            start = start.plus(DatePeriod(days = 7))
        }
        return weeks
    }

    /**
     * The days the comparisons of [period] read in [mode], as one run: the period itself (its days
     * the screen may not hold, e.g. the edges of its weeks) and what they are compared with. For
     * LAST_WEEK, the whole weeks over [period] and the week before the first ([firstDayOfWeek]),
     * so every week-over-week figure has both sides.
     */
    fun readRange(period: DateRange, mode: ComparisonMode, firstDayOfWeek: Int): DateRange = when (mode) {
        ComparisonMode.LAST_WEEK -> {
            val weeks = weeksCovering(period, firstDayOfWeek)
            DateRange(weeks.first().start.minus(DatePeriod(days = 7)), weeks.last().end)
        }
        else -> counterpartRange(period, mode)
    }
}

/** One day's figures as comparisons read them; [netSales] null: no takings recorded (no data). */
data class DayFigures(
    val date: LocalDate,
    val netSales: Double?,
    /** The actual employer labour cost. */
    val labourCost: Double,
    /** What the labour % is measured against (the income, or the takings without a commission); null without takings. */
    val labourBase: Double?,
    /** The hours the SPMH divides the takings by (those worked, or the planned ones while none are counted). */
    val hours: Double,
    /** The day's actual income and costs (its daily result is their difference). */
    val income: Double,
    val costs: Double
) {
    val hasTakings: Boolean get() = netSales != null

    val labourPercent: Double? get() = labourBase?.let { LabourShare.percent(labourCost, it) }
    val spmh: Double? get() = Spmh.of(netSales, hours)
    val result: Double? get() = if (hasTakings) income - costs else null

    fun value(metric: ComparisonMetric): Double? = when (metric) {
        ComparisonMetric.NET_SALES -> netSales
        ComparisonMetric.LABOUR_COST -> if (hasTakings) labourCost else null
        ComparisonMetric.LABOUR_PERCENT -> labourPercent
        ComparisonMetric.SPMH -> spmh
        ComparisonMetric.RESULT -> result
    }

    companion object {
        /**
         * [record]'s actual side (as its daily result counts it when passed through DayResults.resultDay).
         * SH-252: takings entered as 0 (a closed Sunday) are no takings: no data, never the weakest day.
         */
        fun of(record: DailyRecord): DayFigures {
            val takings = record.revenue.realNetto?.takeIf { it != 0.0 }
            return DayFigures(
                date = LocalDate.parse(record.date),
                netSales = takings,
                labourCost = record.laborStats.actual.totalCost,
                labourBase = record.labourBaseReal?.takeIf { takings != null },
                hours = Spmh.actualHours(record.costs.actualHours, record.costs.assignedPlannedHours),
                income = record.incomeReal,
                costs = record.incomeReal - record.resultReal
            )
        }
    }
}

/** A run of days' figures added up (never averaged): the sales, the labour %, SPMH and result of their totals. */
data class PeriodFigures(
    val netSales: Double,
    val labourCost: Double,
    val labourBase: Double,
    val hours: Double,
    val income: Double,
    val costs: Double,
    val days: Int
) {
    val labourPercent: Double? get() = LabourShare.percent(labourCost, labourBase)
    val spmh: Double? get() = Spmh.of(netSales, hours)
    val result: Double get() = income - costs

    fun value(metric: ComparisonMetric): Double? = when (metric) {
        ComparisonMetric.NET_SALES -> netSales
        ComparisonMetric.LABOUR_COST -> labourCost
        ComparisonMetric.LABOUR_PERCENT -> labourPercent
        ComparisonMetric.SPMH -> spmh
        ComparisonMetric.RESULT -> result
    }

    companion object {
        /** The sum of [days] with takings; null when none has any (no data, not 0). */
        fun of(days: List<DayFigures>): PeriodFigures? {
            val traded = days.filter { it.hasTakings }
            if (traded.isEmpty()) return null
            return PeriodFigures(
                netSales = traded.sumOf { it.netSales ?: 0.0 },
                labourCost = traded.sumOf { it.labourCost },
                labourBase = traded.sumOf { it.labourBase ?: 0.0 },
                hours = traded.sumOf { it.hours },
                income = traded.sumOf { it.income },
                costs = traded.sumOf { it.costs },
                days = traded.size
            )
        }
    }
}

/**
 * [current] against [base]: the [absolute] difference and the change in [percent] of the base.
 * Either side missing: no change at all. A base of 0: an absolute difference but no %.
 */
data class Change(val current: Double?, val base: Double?) {
    val absolute: Double? get() = if (current != null && base != null) current - base else null

    /** (current − base) ÷ |base| × 100, so a smaller loss reads as a rise; null when the base is 0 or missing. */
    val percent: Double?
        get() {
            if (current == null || base == null || base == 0.0 || base.isNaN() || current.isNaN()) return null
            return (current - base) / abs(base) * 100.0
        }

    /** Both sides are there. */
    val hasData: Boolean get() = current != null && base != null
}

/** One day against its counterpart. */
data class DayComparison(
    val date: LocalDate,
    val counterpart: Counterpart,
    /** The day's figures; null: no record of the day. */
    val current: DayFigures?,
    val base: DayFigures?,
    /** A public holiday on the day, or on the day it is compared with (the comparison is uneven). */
    val holiday: Boolean = false,
    val baseHoliday: Boolean = false
) {
    /** Both days have takings recorded: the day counts in its period's comparison. */
    val compared: Boolean get() = current?.hasTakings == true && base?.hasTakings == true

    /** The day has takings but the day it is compared with has none (e.g. "no data from last year"). */
    val baseMissing: Boolean get() = current?.hasTakings == true && base?.hasTakings != true

    fun change(metric: ComparisonMetric): Change =
        if (compared) Change(current?.value(metric), base?.value(metric)) else Change(current?.value(metric), null)
}

/** A run of days against the days they are compared with, like for like. */
data class PeriodComparison(val mode: ComparisonMode, val days: List<DayComparison>) {
    /** The days in the period. */
    val periodDays: Int get() = days.size

    /** The days both sides have takings for: the only ones counted on either side. */
    val compared: List<DayComparison> get() = days.filter { it.compared }
    val comparedDays: Int get() = compared.size

    /** The period's side: the compared days added up; null without any. */
    val current: PeriodFigures? get() = PeriodFigures.of(compared.mapNotNull { it.current })
    val base: PeriodFigures? get() = PeriodFigures.of(compared.mapNotNull { it.base })

    fun change(metric: ComparisonMetric): Change = Change(current?.value(metric), base?.value(metric))

    /** Days with a public holiday on either side. */
    val holidays: List<DayComparison> get() = days.filter { it.holiday || it.baseHoliday }

    /** Days with takings whose counterpart has none. */
    val missingBase: Int get() = days.count { it.baseMissing }
}

/** One week against the week before ([comparison], like for like). */
data class WeekComparison(val week: DateRange, val comparison: PeriodComparison) {
    val change: Change get() = comparison.change(ComparisonMetric.NET_SALES)
}

/**
 * The comparisons of the days in [records] (any days, both sides: the screen's month and what was
 * read for it). [isHoliday] marks the public holidays of the workplace's market.
 */
class SalesComparisons(
    records: Collection<DailyRecord>,
    private val isHoliday: (LocalDate) -> Boolean = { false }
) {
    private val days: Map<LocalDate, DayFigures> = records.mapNotNull { record ->
        runCatching { DayFigures.of(record) }.getOrNull()
    }.associateBy { it.date }

    /** [date]'s figures; null without a record of it. */
    fun figures(date: LocalDate): DayFigures? = days[date]

    fun day(date: LocalDate, mode: ComparisonMode): DayComparison = dayAgainst(date, ComparisonCalendar.counterpart(date, mode))

    /** SH-228: [date] against any [counterpart] (the period before, last year); [withBase] false: no base at all. */
    fun dayAgainst(date: LocalDate, counterpart: Counterpart, withBase: Boolean = true): DayComparison = DayComparison(
        date = date,
        counterpart = counterpart,
        current = days[date],
        base = if (withBase) days[counterpart.date] else null,
        holiday = isHoliday(date),
        baseHoliday = withBase && isHoliday(counterpart.date)
    )

    fun period(range: DateRange, mode: ComparisonMode): PeriodComparison = PeriodComparison(mode, range.dates.map { day(it, mode) })

    /** The week of [today] up to it (weeks from [firstDayOfWeek]) against the same days of the week before. */
    fun weekToDate(today: LocalDate, firstDayOfWeek: Int): PeriodComparison =
        period(DateRange(ComparisonCalendar.weekStart(today, firstDayOfWeek), today), ComparisonMode.LAST_WEEK)

    /** The month of [today] up to it against the same number of days in [mode] (each day its counterpart). */
    fun monthToDate(today: LocalDate, mode: ComparisonMode): PeriodComparison =
        period(DateRange(LocalDate(today.year, today.month, 1), today), mode)

    /** Every week over [range] (from [firstDayOfWeek]) against the week before, like for like. */
    fun weeks(range: DateRange, firstDayOfWeek: Int): List<WeekComparison> =
        ComparisonCalendar.weeksCovering(range, firstDayOfWeek).map { week -> WeekComparison(week, period(week, ComparisonMode.LAST_WEEK)) }
}
